import {
	useHubVehicle,
	type VehicleOption as HubVehicleOption,
} from "hooks/api/Hub";
import React, { FormEvent, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { api } from "services/api";
import { notify } from "services/notification";

import kemiraLogo from "assets/images/logo-kemira-black-header.png";
import webcolLogo from "assets/images/logo-webcol-preta.png";
import plusWhiteIcon from "assets/images/plus-white-icon.png";
import previousArrow from "assets/images/prev-arrow.png";
import { MaterialDetails } from "../MaterialDetails";
import * as S from "./styles";

interface OrderData {
	id?: number;
	sale_doc?: string;
	po_number?: string;
	date?: string;
	general_company_id?: number;
	general_client_id?: number;
	general_carrier_id?: number;
	product_qty?: number;
	partial_weight?: number | null;
	product_code?: string | number | null;
	product_description?: string | null;
	product?: {
		code?: string | number | null;
		description?: string | null;
		material_freight_grp?: string | null;
	} | null;
	client?: {
		trade_name?: string;
		address_city?: string;
		address_state?: string;
	};
	company?: { address_city?: string; address_state?: string };
	ship?: { address_city?: string; address_state?: string };
	carrier?: { cnpj_root?: string; cnpj?: string };
}

interface LocationState {
	order?: OrderData;
}

interface VehicleOption {
	id: number;
	duration: number;
	scheduling_vehicle_type_id: number;
	vehicleType?: { name?: string };
}

interface TimeframeResponse {
	docks?: Array<{
		dock?: { id: number; name?: string };
		timeFrames?: string[];
	}>;
}

interface DriverOption {
	id: number;
	name: string;
	document: string;
	birth_date?: string;
}

type FieldName =
	| "vehicleType"
	| "plate"
	| "driverCpf"
	| "birthDate"
	| "driverName"
	| "date"
	| "time";
type FieldErrors = Partial<Record<FieldName, string>>;

const getDeliveriesStorageKey = (order?: OrderData) =>
	order?.id ? `agendamento-fob-deliveries-${order.id}` : null;

const formatDate = (value?: string) => {
	if (!value) return "--";
	const [year, month, day] = value.slice(0, 10).split("-");
	return `${day}/${month}/${year}`;
};

const locationName = (location?: {
	address_city?: string;
	address_state?: string;
}) =>
	location?.address_city && location?.address_state
		? `${location.address_city}/${location.address_state}`
		: "--";

export const AgendamentoFobSchedule: React.FC = () => {
	const history = useHistory();
	const { state } = useLocation<LocationState>();
	const order = state?.order;
	const { getListOptionsVehicles } = useHubVehicle({
		nameVariant: "vehicle-subtype-plate-and-axis-count",
	});
	const [deliveries, setDeliveries] = useState<OrderData[]>([]);
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [newDeliveryNumber, setNewDeliveryNumber] = useState("");
	const [deliveryError, setDeliveryError] = useState("");
	const [isAddingDelivery, setIsAddingDelivery] = useState(false);
	const [driverName, setDriverName] = useState("");
	const [plate, setPlate] = useState("");
	const [driverCpf, setDriverCpf] = useState("");
	const [birthDate, setBirthDate] = useState("");
	const [vehicleType, setVehicleType] = useState("");
	const [date, setDate] = useState("");
	const [time, setTime] = useState("");
	const [drivers, setDrivers] = useState<DriverOption[]>([]);
	const [loadingDrivers, setLoadingDrivers] = useState(false);
	const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
	const [hubVehicles, setHubVehicles] = useState<HubVehicleOption[]>([]);
	const [hubVehicleId, setHubVehicleId] = useState("");
	const [timeframes, setTimeframes] = useState<
		Array<{ value: string; label: string }>
	>([]);
	const [loadingVehicles, setLoadingVehicles] = useState(false);
	const [loadingHubVehicles, setLoadingHubVehicles] = useState(false);
	const [loadingTimeframes, setLoadingTimeframes] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
	const formatWeight = (delivery: OrderData) =>
		delivery.product_qty == null
			? "--"
			: new Intl.NumberFormat("pt-BR", {
					minimumFractionDigits: 2,
				}).format(delivery.product_qty);

	const clearFieldError = (field: FieldName) => {
		setFieldErrors((current) => {
			if (!current[field]) return current;
			const next = { ...current };
			delete next[field];
			return next;
		});
	};

	useEffect(() => {
		if (!order) return;

		const storageKey = getDeliveriesStorageKey(order);
		if (!storageKey) {
			setDeliveries([order]);
			return;
		}

		try {
			const stored = sessionStorage.getItem(storageKey);
			const parsed = stored ? (JSON.parse(stored) as OrderData[]) : [];
			const storedDeliveries = Array.isArray(parsed) ? parsed : [];
			const hasMainOrder = storedDeliveries.some(
				(delivery) => delivery.id === order.id,
			);
			const restored = hasMainOrder
				? storedDeliveries
				: [order, ...storedDeliveries];

			setDeliveries(restored);
			sessionStorage.setItem(storageKey, JSON.stringify(restored));
		} catch {
			setDeliveries([order]);
		}
	}, [order]);

	useEffect(() => {
		if (!order?.general_company_id) return;
		setLoadingHubVehicles(true);
		api.get<{ vehicles?: VehicleOption[] }>(
			"/agendamento-fob/scheduling/vehicles",
			{
				params: {
					general_company_id: order.general_company_id,
					maxWeight:
						order.product_qty || order.partial_weight || undefined,
					all: true,
				},
			},
		)
			.then(({ data }) =>
				setVehicles(Array.isArray(data) ? data : data.vehicles || []),
			)
			.catch(() =>
				notify(
					"error",
					"Não foi possível carregar os tipos de veículo.",
				),
			)
			.finally(() => setLoadingVehicles(false));
	}, [order]);

	useEffect(() => {
		setHubVehicles([]);
		setHubVehicleId("");
		setPlate("");

		if (!order?.carrier?.cnpj_root) return;

		setLoadingVehicles(true);
		getListOptionsVehicles({ carrier_cnpj_root: order.carrier.cnpj_root })
			.then((options) => setHubVehicles(options))
			.catch(() =>
				notify(
					"error",
					"Não foi possível carregar as placas da transportadora.",
				),
			)
			.finally(() => setLoadingHubVehicles(false));
	}, [order?.carrier?.cnpj_root]);

	useEffect(() => {
		if (!order?.general_carrier_id) return;
		setLoadingDrivers(true);
		api.get<{ drivers: DriverOption[] }>(
			"/agendamento-fob/scheduling/drivers",
			{
				params: { general_carrier_id: order.general_carrier_id },
			},
		)
			.then(({ data }) => setDrivers(data.drivers || []))
			.catch(() =>
				notify(
					"error",
					"Não foi possível carregar os motoristas da transportadora.",
				),
			)
			.finally(() => setLoadingDrivers(false));
	}, [order]);

	useEffect(() => {
		const selectedVehicle = vehicles.find(
			(vehicle) => String(vehicle.id) === vehicleType,
		);
		if (
			!order?.general_company_id ||
			!order.general_carrier_id ||
			!selectedVehicle ||
			!date ||
			!plate
		) {
			setTimeframes([]);
			setTime("");
			return;
		}

		setLoadingTimeframes(true);
		api.get<TimeframeResponse>("/agendamento-fob/scheduling/timeframes", {
			params: {
				general_carrier_id: order.general_carrier_id,
				company_id: order.general_company_id,
				totalDuration: selectedVehicle.duration,
				target_date: date,
				type_dock: "FOB",
				vehicle_plate: plate,
			},
		})
			.then(({ data }) => {
				const options = (data.docks || []).flatMap((item) =>
					(item.timeFrames || []).map((timestamp) => ({
						value: `${item.dock?.id}+${timestamp}`,
						label: `${item.dock?.name || "Doca"} - ${timestamp.slice(11, 16)}`,
					})),
				);
				setTimeframes(options);
				setTime("");
			})
			.catch(() => {
				setTimeframes([]);
				notify(
					"error",
					"Não foi possível carregar os horários disponíveis.",
				);
			})
			.finally(() => setLoadingTimeframes(false));
	}, [date, order, plate, vehicleType, vehicles]);

	const handleAddDelivery = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const value = newDeliveryNumber.trim();

		if (!value) {
			setDeliveryError("Informe o número da Sales Order.");
			return;
		}

		if (deliveries.some((delivery) => delivery.po_number === value)) {
			setDeliveryError("Essa Sales Order já foi adicionada.");
			return;
		}

		setIsAddingDelivery(true);
		try {
			const { data } = await api.get<OrderData>(
				`/agendamento-fob/orders-fob/po-number/${encodeURIComponent(value)}`,
			);
			const nextDeliveries = [...deliveries, data];
			setDeliveries(nextDeliveries);
			const storageKey = getDeliveriesStorageKey(order);
			if (storageKey)
				sessionStorage.setItem(
					storageKey,
					JSON.stringify(nextDeliveries),
				);
			setNewDeliveryNumber("");
			setDeliveryError("");
			setIsAddModalOpen(false);
			notify("success", "Sales Order adicionada com sucesso.");
		} catch (error: any) {
			notify(
				"error",
				error?.response?.data?.error?.message ||
					error?.response?.data?.message ||
					"Não foi possível buscar a Sales Order.",
			);
		} finally {
			setIsAddingDelivery(false);
		}
	};

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const selectedVehicle = vehicles.find(
			(vehicle) => String(vehicle.id) === vehicleType,
		);
		const errors: FieldErrors = {};
		if (!selectedVehicle)
			errors.vehicleType = "Selecione o tipo de veículo.";
		if (!plate) errors.plate = "Selecione a placa.";
		if (!driverCpf) errors.driverCpf = "Selecione o motorista.";
		if (!birthDate) errors.birthDate = "Informe a data de nascimento.";
		if (!driverName) errors.driverName = "Selecione o motorista.";
		if (!date) errors.date = "Informe a data da coleta.";
		if (!time) errors.time = "Selecione o horário.";

		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) {
			return;
		}
		if (!selectedVehicle) return;
		if (!order) {
			notify("error", "Nenhuma order foi selecionada.");
			return;
		}

		setIsSubmitting(true);
		try {
			const { data: scheduling } = await api.post(
				"/agendamento-fob/scheduling",
				{
					orders_ids: deliveries
						.map((delivery) => delivery.id)
						.join(","),
					general_company_id: order.general_company_id,
					general_client_id: order.general_client_id,
					general_carrier_id: order.general_carrier_id,
					driver_document: driverCpf,
					driver_name: driverName,
					driver_birth_date: birthDate,
					scheduling_vehicle_id: selectedVehicle.id,
					vehicle_type_id: selectedVehicle.scheduling_vehicle_type_id,
					vehicle_plate: plate,
					duration: selectedVehicle.duration,
					dock_timeframe: time,
					emails: [],
				},
			);
			notify("success", "Agendamento criado com sucesso.");
			const storageKey = getDeliveriesStorageKey(order);
			if (storageKey) sessionStorage.removeItem(storageKey);
			const selectedTimeframe = timeframes.find(
				(option) => option.value === time,
			);
			const [dockName] = selectedTimeframe?.label.split(" - ") || [];
			const confirmationState = {
				scheduling,
				order,
				deliveries,
				dockName,
				dockTime: time.split("+")[1],
			};
			sessionStorage.setItem(
				"agendamento-fob-confirmation",
				JSON.stringify(confirmationState),
			);
			window.location.assign(
				`/agendamento-fob/confirmation/${scheduling?.id || ""}`,
			);
		} catch (error: any) {
			notify(
				"error",
				error?.response?.data?.error?.message ||
					error?.response?.data?.message ||
					"Não foi possível criar o agendamento.",
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!order) {
		return (
			<S.Page>
				<S.EmptyState>
					<S.Logo src={kemiraLogo} alt="Kemira" />
					<p>Nenhuma Sales Order foi selecionada.</p>
					<S.PrimaryButton
						type="button"
						onClick={() => history.push("/agendamento-fob")}
					>
						Voltar para busca
					</S.PrimaryButton>
				</S.EmptyState>
			</S.Page>
		);
	}

	return (
		<S.Page>
			<S.Container>
				<S.Header>
					<S.Logo src={kemiraLogo} alt="Kemira" />
					<S.HeaderActions>
						<S.BackButton
							type="button"
							onClick={() => history.push("/agendamento-fob")}
						>
							<S.PreviousArrow
								src={previousArrow}
								alt=""
								aria-hidden="true"
							/>{" "}
							Voltar
						</S.BackButton>
						<S.AccessText>
							Acesso exclusivo para clientes Kemira
						</S.AccessText>
					</S.HeaderActions>
				</S.Header>
				<S.Content>
					<S.SectionHeadingRow>
						<S.SectionTitle>
							Sales Orders ( {deliveries.length} )
						</S.SectionTitle>
						<S.AddButton
							type="button"
							onClick={() => setIsAddModalOpen(true)}
						>
							<S.PlusIcon
								src={plusWhiteIcon}
								alt=""
								aria-hidden="true"
							/>{" "}
							Adicionar Sales Order
						</S.AddButton>
					</S.SectionHeadingRow>
					{deliveries?.map((delivery) => (
						<MaterialDetails
							key={`${delivery.po_number}-${delivery.sale_doc}`}
							order={delivery}
						>
							<S.DeliveryCard>
							<S.DeliveryItem>
								<strong>Sales Order</strong>
								<span>{delivery.po_number || "--"}</span>
							</S.DeliveryItem>
							<S.DeliveryItem>
								<strong>Data</strong>
								<span>{formatDate(delivery.date)}</span>
							</S.DeliveryItem>
							<S.DeliveryItem>
								<strong>Peso</strong>
								<span>{formatWeight(delivery)}</span>
							</S.DeliveryItem>
							<S.DeliveryItem>
								<strong>Cliente</strong>
								<span>
									{delivery.client?.trade_name || "--"}
								</span>
							</S.DeliveryItem>
							<S.DeliveryItem>
								<strong>Origem</strong>
								<span>{locationName(delivery.company)}</span>
							</S.DeliveryItem>
							<S.DeliveryItem>
								<strong>Destino</strong>
								<span>
									{locationName(
										delivery.ship || delivery.client,
									)}
								</span>
							</S.DeliveryItem>
							</S.DeliveryCard>
						</MaterialDetails>
					))}
					<S.InfoSectionTitle>
						Informações do agendamento
					</S.InfoSectionTitle>
					<S.FormCard id="schedule-form" onSubmit={handleSubmit}>
						<S.FieldGrid>
							<S.Field>
								<label htmlFor="vehicle-type">
									Tipo de veículo
								</label>
								<S.Select
									id="vehicle-type"
									value={vehicleType}
									disabled={loadingVehicles}
									aria-invalid={Boolean(
										fieldErrors.vehicleType,
									)}
									onChange={(event) => {
										setVehicleType(event.target.value);
										clearFieldError("vehicleType");
									}}
								>
									<option value="">
										{loadingVehicles
											? "Carregando..."
											: "Selecione"}
									</option>
									{vehicles.map((vehicle) => (
										<option
											key={vehicle.id}
											value={vehicle.id}
										>
											{vehicle.vehicleType?.name ||
												`Veículo ${vehicle.id}`}
										</option>
									))}
								</S.Select>
								{fieldErrors.vehicleType && (
									<S.FieldError>
										{fieldErrors.vehicleType}
									</S.FieldError>
								)}
							</S.Field>
							<S.Field>
								<label htmlFor="plate">Placa</label>
								<S.Select
									id="plate"
									value={hubVehicleId}
									disabled={
										loadingHubVehicles ||
										!hubVehicles.length
									}
									aria-invalid={Boolean(fieldErrors.plate)}
									onChange={(event) => {
										const selected = hubVehicles.find(
											(vehicle) =>
												String(vehicle.id) ===
												event.target.value,
										);
										setHubVehicleId(event.target.value);
										setPlate(selected?.vehicle_plate || "");
										clearFieldError("plate");
									}}
								>
									<option value="">
										{loadingHubVehicles
											? "Carregando..."
											: hubVehicles.length
												? "Selecione"
												: "Nenhuma placa disponível"}
									</option>
									{hubVehicles.map((vehicle) => (
										<option
											key={vehicle.id}
											value={vehicle.id}
										>
											{vehicle.name}
										</option>
									))}
								</S.Select>
								{fieldErrors.plate && (
									<S.FieldError>
										{fieldErrors.plate}
									</S.FieldError>
								)}
							</S.Field>
							<S.Field>
								<label htmlFor="cpf">CPF do motorista</label>
								<S.Input
									id="cpf"
									value={driverCpf}
									readOnly
									aria-invalid={Boolean(
										fieldErrors.driverCpf,
									)}
								/>
								{fieldErrors.driverCpf && (
									<S.FieldError>
										{fieldErrors.driverCpf}
									</S.FieldError>
								)}
							</S.Field>
							<S.Field>
								<label htmlFor="birth-date">
									Data de nascimento
								</label>
								<S.Input
									id="birth-date"
									type="date"
									value={birthDate}
									aria-invalid={Boolean(
										fieldErrors.birthDate,
									)}
									onChange={(event) => {
										setBirthDate(event.target.value);
										clearFieldError("birthDate");
									}}
								/>
								{fieldErrors.birthDate && (
									<S.FieldError>
										{fieldErrors.birthDate}
									</S.FieldError>
								)}
							</S.Field>
							<S.Field full>
								<label htmlFor="driver-name">
									Nome completo do motorista
								</label>
								<S.Select
									id="driver-name"
									value={driverCpf}
									disabled={loadingDrivers}
									aria-invalid={Boolean(
										fieldErrors.driverName,
									)}
									onChange={(event) => {
										const selected = drivers.find(
											(driver) =>
												driver.document ===
												event.target.value,
										);
										setDriverCpf(selected?.document || "");
										setDriverName(selected?.name || "");
										if (selected?.birth_date)
											setBirthDate(
												selected.birth_date.slice(
													0,
													10,
												),
											);
										clearFieldError("driverName");
										clearFieldError("driverCpf");
										clearFieldError("birthDate");
									}}
								>
									<option value="">
										{loadingDrivers
											? "Carregando..."
											: "Selecione"}
									</option>
									{drivers.map((driver) => (
										<option
											key={driver.id}
											value={driver.document}
										>
											{driver.name} ({driver.document})
										</option>
									))}
								</S.Select>
								{fieldErrors.driverName && (
									<S.FieldError>
										{fieldErrors.driverName}
									</S.FieldError>
								)}
							</S.Field>
							<S.Field>
								<label htmlFor="schedule-date">
									Data da coleta
								</label>
								<S.Input
									id="schedule-date"
									type="date"
									value={date}
									aria-invalid={Boolean(fieldErrors.date)}
									onChange={(event) => {
										setDate(event.target.value);
										clearFieldError("date");
									}}
								/>
								{fieldErrors.date && (
									<S.FieldError>
										{fieldErrors.date}
									</S.FieldError>
								)}
							</S.Field>
							<S.Field>
								<label htmlFor="schedule-time">Hora</label>
								<S.Select
									id="schedule-time"
									value={time}
									disabled={
										loadingTimeframes ||
										!date ||
										!vehicleType ||
										!plate
									}
									aria-invalid={Boolean(fieldErrors.time)}
									onChange={(event) => {
										setTime(event.target.value);
										clearFieldError("time");
									}}
								>
									<option value="">
										{loadingTimeframes
											? "Carregando..."
											: "Selecione"}
									</option>
									{timeframes.map((option) => (
										<option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</option>
									))}
								</S.Select>
								{fieldErrors.time && (
									<S.FieldError>
										{fieldErrors.time}
									</S.FieldError>
								)}
							</S.Field>
						</S.FieldGrid>
					</S.FormCard>
					<S.Actions>
						<S.CancelButton
							type="button"
							disabled={isSubmitting}
							onClick={() => {
								const storageKey =
									getDeliveriesStorageKey(order);
								if (storageKey)
									sessionStorage.removeItem(storageKey);
								history.push("/agendamento-fob");
							}}
						>
							Cancelar
						</S.CancelButton>
						<S.PrimaryButton
							type="submit"
							form="schedule-form"
							disabled={isSubmitting}
							aria-busy={isSubmitting}
						>
							{isSubmitting && (
								<S.LoadingSpinner aria-hidden="true" />
							)}
							{isSubmitting ? "Agendando..." : "Agendar coleta"}
						</S.PrimaryButton>
					</S.Actions>
				</S.Content>
				{isAddModalOpen && (
					<S.ModalOverlay
						onClick={() =>
							!isAddingDelivery && setIsAddModalOpen(false)
						}
					>
						<S.Modal
							role="dialog"
							aria-modal="true"
							aria-labelledby="add-delivery-title"
							onClick={(event) => event.stopPropagation()}
						>
							<S.ModalTitle id="add-delivery-title">
								Adicionar Sales Order
							</S.ModalTitle>
							<S.ModalDescription>
								Informe o número da Sales Order para buscar os
								dados.
							</S.ModalDescription>
							<S.ModalForm onSubmit={handleAddDelivery}>
								<S.ModalLabel htmlFor="new-delivery-number">
									Número da Sales Order
								</S.ModalLabel>
								<S.ModalInput
									id="new-delivery-number"
									autoFocus
									value={newDeliveryNumber}
									aria-invalid={Boolean(deliveryError)}
									onChange={(event) => {
										setNewDeliveryNumber(
											event.target.value,
										);
										if (deliveryError) setDeliveryError("");
									}}
									placeholder="Digite o número da Sales Order"
								/>
								{deliveryError && (
									<S.FieldError>{deliveryError}</S.FieldError>
								)}
								<S.ModalActions>
									<S.ModalCancel
										type="button"
										disabled={isAddingDelivery}
										onClick={() => setIsAddModalOpen(false)}
									>
										Cancelar
									</S.ModalCancel>
									<S.ModalSubmit
										type="submit"
										disabled={isAddingDelivery}
									>
										{isAddingDelivery
											? "Buscando..."
											: "Buscar Sales Order"}
									</S.ModalSubmit>
								</S.ModalActions>
							</S.ModalForm>
						</S.Modal>
					</S.ModalOverlay>
				)}
				<S.Footer>
					<span>Powered by GS Tecnologia e Inovação®</span>
					<img src={webcolLogo} alt="Webcol" />
				</S.Footer>
			</S.Container>
		</S.Page>
	);
};

export default AgendamentoFobSchedule;
